import React from 'react';

// Models
import { QuoteProcurementPartInfoResponse } from '@/main/models/response';
import WarningIcon from '@mui/icons-material/Warning';

// Styles
import * as S from './styles';

// Components
import QuoteProcurementPartInfo, {
  RenderPartInformation,
  RenderQuoteItemInformation
} from './quoteProcurementPartInfo';

// Utils
import { getPartTypeAndBrandText } from './quoteProcurementPartInfoUtils';
import { isEmpty, isNullUndefined } from '@/main/services/utils';
import colors from '@/main/style/colors';
import { ClickAwayListener, IconButton } from '@mui/material';
import TooltipComponent from '@/main/components/shared/tooltip/tooltipComponent';
import useWindowDimensions from '@/main/hooks/useKogWindowDimension';
import { ResolutionsEnum } from '@/main/services/enum/ResolutionsEnum';
import { PartTypeEnum } from '@/main/services/enum/PartTypeEnum';

export interface IRenderSupplierInventoryInformation {
  supplierInventoryPartTitle: string | undefined;
  partType: string | undefined;
  partBrand: string | undefined;
  showLineSupplierInfo?: { info: 'all' | 'partType' | '2lines' };
  partTypeQuoteItem: string | undefined;
}
export function RenderSupplierInventoryInformation({
  supplierInventoryPartTitle,
  partBrand,
  partType,
  showLineSupplierInfo,
  partTypeQuoteItem
}: IRenderSupplierInventoryInformation) {
  function renderContent() {
    let supplierInventoryInfo: string = '';

    if (showLineSupplierInfo) {
      if (showLineSupplierInfo?.info === 'all') {
        if (supplierInventoryPartTitle) {
          supplierInventoryInfo = supplierInventoryPartTitle;

          // if (partType) {
          //   supplierInventoryInfo +=
          //     ' - ' +
          //     getPartTypeAndBrandText(
          //       partType,
          //       partBrand,
          //       true,
          //       partTypeQuoteItem
          //     );
          // }

          return <>{supplierInventoryInfo}</>;
        }
        return (
          <>
            {getPartTypeAndBrandText(
              partType,
              partBrand,
              true,
              partTypeQuoteItem
            )}
          </>
        );
      } else if (showLineSupplierInfo?.info === 'partType') {
        return (
          <>
            {getPartTypeAndBrandText(
              partType,
              partBrand,
              true,
              partTypeQuoteItem
            )}
          </>
        );
      }
    }

    return <></>;
  }

  return renderContent();
}

interface QuoteProcurementPartInfoColumnProps {
  data: QuoteProcurementPartInfoResponse;
  showLineSupplierInfo?: { info: 'all' | 'partType' | '2lines' };
  showOnlyPartType?: boolean;
  showIconNotes?: boolean;
  isEllipsisText?: boolean;
  isTooltip?: boolean;
  isHyperLink?: boolean;
  onClick?: () => void;
}
export default function QuoteProcurementPartInfoColumn({
  data,
  showLineSupplierInfo,
  showOnlyPartType = false,
  showIconNotes = false,
  isEllipsisText,
  isTooltip = false,
  isHyperLink = false,
  onClick
}: QuoteProcurementPartInfoColumnProps) {
  const {
    quoteItemDescription,
    partName,
    quoteItemPartNumber,
    partPosition,
    partType,
    supplierInventoryPartTitle,
    partBrand,
    notes,
    partTypeQuoteItem,
    quoteSupplierItemPartNumber
  } = data;

  const [isOpenTooltipNotes, setIsOpenTooltipNotes] = React.useState(false);
  const { width } = useWindowDimensions();

  const isTireGlass =
    data.idPartType == PartTypeEnum.Tire ||
    data.idPartType == PartTypeEnum.Glass;
  const showIcon = showIconNotes && (notes || isTireGlass);

  const tireGlassInfoTooltip = renderGlassTireText()
    .split(' ')
    .filter(Boolean)
    .join(' ');
  const tireGlassTypeTooltip = !isEmpty(partType) && partType !== 'undefined'
    ? partTypeQuoteItem &&
      partTypeQuoteItem !== 'undefined' &&
      partTypeQuoteItem != partType
      ? `${partType} (${partTypeQuoteItem})`
      : partType
    : !isEmpty(partTypeQuoteItem) && partTypeQuoteItem !== 'undefined'
      ? `(${partTypeQuoteItem})`
      : '';
  const tireGlassPartTooltip = [
    isEmpty(partName) ? tireGlassTypeTooltip : partName,
    isEmpty(partPosition) ? '' : `- ${partPosition}`,
    tireGlassInfoTooltip
  ]
    .filter(Boolean)
    .join(' ');

  function renderGlassTireText() {
    const addHifen = isEmpty(partPosition) ? '' : '- ';

    if (!isEmpty(data.glassInfo))
      return `${
        partTypeQuoteItem &&
        partTypeQuoteItem !== 'undefined' &&
        partTypeQuoteItem != partType
          ? ` (${partTypeQuoteItem})`
          : ''
      } ${isEmpty(partBrand) ? '' : `(${partBrand})`} ${data.glassInfo}`;

    if (!isEmpty(data.tireInfo))
      return `${
        partTypeQuoteItem &&
        partTypeQuoteItem !== 'undefined' &&
        partTypeQuoteItem != partType
          ? ` (${partTypeQuoteItem})`
          : ''
      } ${data.tireInfo}`;

    return '';
  }

  return (
    <S.ContainerQuoteProcurementPartInfo
      style={{
        cursor: isHyperLink ? 'pointer' : 'default',
        color: isHyperLink ? '#1976d2' : 'inherit'
      }}
      onClick={isHyperLink ? onClick : undefined}>
      <S.AreaRenderQuoteItemInformation>
        <S.SpanTitlePart $isEllipsisText={false} $showIcon={!!showIcon}>
          <span className="text-with-icon">
            <span className="text">
              <RenderQuoteItemInformation
                quoteItemDescription={quoteItemDescription}
                quoteItemPartNumber={quoteItemPartNumber}
                notes={notes}
                partName={partName}
              />
            </span>

            {showIcon && (
              <span className="icon">
                <ClickAwayListener
                  onClickAway={() => setIsOpenTooltipNotes(false)}>
                  <div>
                    <TooltipComponent
                      open={isOpenTooltipNotes}
                      maxWidth={'100%'}
                      title={
                        <QuoteProcurementPartInfo
                          isEllipsisText={false}
                          data={data}
                          renderType="column"
                          showLineSupplierInfo={{ info: 'all' }}
                        />
                      }
                      placement="top"
                      onClose={() => setIsOpenTooltipNotes(false)}
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener>
                      <IconButton
                        id="id-quoteProcurementPartInfoColumn-iconButton"
                        onClick={e => {
                          e.stopPropagation();
                          e.preventDefault();
                          // only mobile
                          if (width < ResolutionsEnum.desktop) {
                            setIsOpenTooltipNotes(prev => !prev);
                          }
                        }}>
                        <WarningIcon
                          style={{ color: colors.orange }}
                          fontSize="small"
                        />
                      </IconButton>
                    </TooltipComponent>
                  </div>
                </ClickAwayListener>
              </span>
            )}
          </span>
        </S.SpanTitlePart>
      </S.AreaRenderQuoteItemInformation>

      <div className="d-flex supplierInventoryPartName">
        <TooltipComponent
          disableFocusListener={isTooltip}
          disableTouchListener={isTooltip}
          disableHoverListener={isTooltip}
          maxWidth="100%"
          placement="top"
          title={
            isTooltip ? (
              <S.TooltipSpanTitlePart>{`${isEmpty(partName) ? '' : partName} ${
                isEmpty(partPosition) ? '' : `- ${partPosition}`
              } ${renderGlassTireText()}`}</S.TooltipSpanTitlePart>
            ) : (
              ''
            )
          }
          stylePopper={partName}>
          <S.SpanTitlePart
            className="partName"
            $isEllipsisText={isEllipsisText}>
            <span
              className="text-content"
              title={
                isTireGlass && tireGlassPartTooltip
                  ? tireGlassPartTooltip
                  : undefined
              }>
              <RenderPartInformation
                partName={showOnlyPartType ? undefined : partName}
                partPosition={showOnlyPartType ? undefined : partPosition}
                quoteItemDescription={quoteItemDescription}
                partType={partType}
                partTypeQuoteItem={partTypeQuoteItem}
                quoteSupplierItemPartNumber={quoteSupplierItemPartNumber}
                showPartTypeAndBrandText={
                  showLineSupplierInfo &&
                  showLineSupplierInfo.info === '2lines' &&
                  !isTireGlass
                }
                partBrand={partBrand}
              />
              {isTireGlass && ` ${renderGlassTireText()}`}
            </span>
          </S.SpanTitlePart>
        </TooltipComponent>
      </div>

      {showLineSupplierInfo &&
        showLineSupplierInfo.info !== '2lines' &&
        !isTireGlass && (
          <div className="d-flex supplierInventoryPartTitle">
            <S.SpanTitlePart $isEllipsisText={isEllipsisText}>
              <RenderSupplierInventoryInformation
                supplierInventoryPartTitle={supplierInventoryPartTitle}
                partType={partType}
                partBrand={partBrand}
                showLineSupplierInfo={showLineSupplierInfo}
                partTypeQuoteItem={partTypeQuoteItem}
              />
            </S.SpanTitlePart>
          </div>
        )}
    </S.ContainerQuoteProcurementPartInfo>
  );
}
